// PullRequestCard — render compartilhado de um PR (DEM-2037).
//
// Extraído de src/pages/PullRequestsPage.tsx (anti-duplicação — MUST_FIX do
// consenso): a aba PRs do DemandDetailPanel REUSA exatamente este card em vez
// de recriar o fetch/render do zero. A PullRequestsPage passa a consumir este
// componente (com seu slot de ações de transição); a aba PRs da demanda usa o
// mesmo card SEM ações (read-only) + link clicável pro GitHub.
//
// O fetch/listagem fica com cada caller (página: api.listPullRequests; aba da
// demanda: api.getDemandPullRequests) — o que é COMPARTILHADO e não-duplicado é
// o STATUS_CONFIG + a anatomia visual do card, que é o que importava.

import { ArrowRight, Bot, Clock, Eye, ExternalLink } from 'lucide-react';
import type { PullRequest } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { PR_STATUS_CONFIG, githubPrUrl } from './pr-display';

interface PullRequestCardProps {
  pr: PullRequest;
  /** Slot de ações (ex.: botões de transição na PullRequestsPage). */
  actions?: React.ReactNode;
  /** Exibe link clicável pro GitHub (aba PRs da demanda — DEM-2037). */
  showGithubLink?: boolean;
}

export function PullRequestCard({ pr, actions, showGithubLink = false }: PullRequestCardProps) {
  const config = PR_STATUS_CONFIG[pr.status];
  const Icon = config.icon;
  const ghUrl = showGithubLink ? githubPrUrl(pr) : null;

  return (
    <Card hoverable className="p-5" data-testid="pr-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
              pr.status === 'merged'
                ? 'bg-raios-accent/15'
                : pr.status === 'closed'
                  ? 'bg-raios-danger/15'
                  : 'bg-raios-elevated',
            )}
          >
            <Icon
              className={cn(
                'h-4.5 w-4.5',
                pr.status === 'merged'
                  ? 'text-raios-accent-text'
                  : pr.status === 'closed'
                    ? 'text-raios-danger-text'
                    : 'text-raios-text-secondary',
              )}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {pr.pr_number != null && (
                <span className="text-sm font-semibold text-raios-text">#{pr.pr_number}</span>
              )}
              <span className="text-sm font-medium text-raios-text">{pr.branch_source}</span>
              <ArrowRight className="h-3 w-3 text-raios-muted shrink-0" />
              <span className="text-sm text-raios-muted">{pr.branch_target}</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge variant={config.variant} className="text-[10px]">
                {config.label}
              </Badge>
              <span className="text-[10px] text-raios-muted flex items-center gap-1">
                <Bot className="h-3 w-3" />
                {pr.author_agent}
              </span>
              <span className="text-[10px] text-raios-muted">{pr.repo}</span>
              {pr.reviewer_agent && (
                <span className="text-[10px] text-raios-muted flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {pr.reviewer_agent}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {ghUrl && (
            <a
              href={ghUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-raios-accent-text hover:underline flex items-center gap-1"
              data-testid="pr-github-link"
            >
              <ExternalLink className="h-3 w-3" />
              GitHub
            </a>
          )}
          <span className="text-xs text-raios-muted flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {new Date(pr.created_at).toLocaleDateString('pt-BR')}{' '}
            {new Date(pr.created_at).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>

      {actions}
    </Card>
  );
}
